// Collars After State — preview/apply ROW model + mapper (TMHQ, frontend-only).
//
// This file is owned by the PREVIEW unit (ets-collars-after-state-preview) and imported by the APPLY unit.
// It is the SINGLE place that turns the diff-service output (CollarsDiffResult) into the three-column row
// shape the cloned after-state table renders (Before / Transaction / After), one row per leaf group.
//
// Parity note: this mirrors the Hedging Grid after-state preview row shape (AfterStatePreviewRow / its
// before|transaction|after arrays) — but it is RE-DERIVED here, never imported from hedging-grid. The HG
// component is a different unit (different data source); only the visual table shape is shared.
//
// PRICE-FREE: rows carry NO price/quote field. The read-only preview shows positions only; prices are
// collected elsewhere (the Apply dialog's actual-fill input, or the review pop-up's ACQUISITION-mode
// Fill Px inputs — held in the component, not on these rows).

import { parseOptionTicker } from '../../options-common/options.model';
import { isVoid } from '../../utils';
import {
   CollarsDiffResult,
   CollarsEditableLeg,
   CollarsAfterStateLeg,
   CollarsTransactionDelta
} from './collars-after-state.models';

// Leg kinds (match CollarsPositionDto.kind / RecordCollarsTransaction.kind).
const KIND_ASSET = 0;

/** One Before/After leg as the cloned table renders it (Qty / Side / Type / Expiration / Strike). */
export interface CollarsPreviewLeg {
   ticker: string;          // verbatim tradingInstrumentCode (the ASSET table shows it; options show attrs)
   kind: number;            // 0 = Asset, 1 = Option (drives the asset-vs-option table template)
   qty: number;             // positive magnitude
   side: number;            // 1 = Buy/Long, -1 = Sell/Short (rendered via etsQtyToMarketSide off sign*qty)
   type?: string;           // 'Call' | 'Put' (option legs); undefined/empty for asset legs
   expiration?: any;        // option legs only (date string the etsFriendlyDatePipe formats)
   strike?: number;         // option legs only
}

/** One Transaction leg as the cloned table renders it (Action / Qty / Expiration / Strike). Carries the
 *  full BOOKING identity (group/side) so the review's acquisition mode can turn an entered fill price
 *  straight into a RecordCollarsTransaction without re-deriving the diff. */
export interface CollarsPreviewTxnLeg {
   operation: 'Open' | 'Close' | 'Increase' | 'Decrease';
   groupBucketId: string;   // the leaf group the booking routes to
   ticker: string;          // verbatim tradingInstrumentCode (the ASSET table shows it; booking uses it)
   kind: number;            // 0 = Asset, 1 = Option
   side: number;            // 1 = Buy, -1 = Sell (the booking leg's side)
   qty: number;             // positive magnitude to trade
   type?: string;           // 'Call' | 'Put' (option legs); undefined/empty for asset legs
   expiration?: any;        // option legs only
   strike?: number;         // option legs only
}

/** One group's three columns — the unit the cloned .row renders (Before | Transaction | After). */
export interface CollarsAfterStateRow {
   groupBucketId: string;   // the leaf group this row represents
   label?: string;          // leaf-group NAME (resolved via context; never show the raw bucket id)
   color?: string;          // leaf-group colour (the .row-title / column tint)
   isAsset: boolean;        // true when EVERY leg in the row is an asset -> render the simpler asset table
   unchanged: boolean;      // no derived transaction (before == after) -> rendered dimmed/readonly
   comboBucketId?: string;  // parent combo (drives the combo separators at portfolio scope)
   comboName?: string;
   before: CollarsPreviewLeg[];
   transaction: CollarsPreviewTxnLeg[];
   after: CollarsPreviewLeg[];
}

/**
 * Display context the CALLER supplies (both dialogs already hold the loaded bucket tree): bucket-id -> name
 * for the row titles, and group -> parent-combo for the portfolio-scope combo separators. Without it the
 * mapper falls back to neutral labels — it NEVER renders a raw GUID.
 */
export interface CollarsAfterStateRowsContext {
   groupNames?: { [groupBucketId: string]: string };
   groupCombos?: { [groupBucketId: string]: { comboBucketId: string; comboName: string } };
}

// --- mapping -------------------------------------------------------------------------------------------

/**
 * Map the diff-service result into per-group preview rows.
 *
 *   Before      = the scope's current live legs (diff.before).
 *   Transaction = the derived deltas, grouped per leaf group (diff.transaction[].deltas).
 *   After       = the edited / saved end-state legs (diff.after) — each carries its OWN groupBucketId.
 *
 * Every column is keyed by leaf group so a row holds ONE group's Before+Transaction+After together. Legs
 * that only carry a tradingInstrumentCode (current positions, whose DTO holds no strike/type/expiration —
 * those live in the ticker code) are enriched via parseOptionTicker.
 *
 * Rows that end up empty in ALL THREE columns are dropped (an untouched, position-less group adds noise).
 * Rows are ordered by first-seen combo, then first-seen group within the combo, so the template can render
 * a combo separator wherever consecutive rows change combo.
 */
export function toAfterStateRows(
   diff: CollarsDiffResult,
   context?: CollarsAfterStateRowsContext
): CollarsAfterStateRow[] {

   if (isVoid(diff)) {
      return [];
   }

   const names = (context && context.groupNames) || {};
   const combos = (context && context.groupCombos) || {};

   const beforeLegs = diff.before || [];
   const afterLegs = diff.after || [];
   const groupTxns = diff.transaction || [];

   // Index Before/After legs by leaf group, preserving first-seen group order.
   const groupOrder: string[] = [];
   const beforeByGroup: { [groupBucketId: string]: CollarsPreviewLeg[] } = {};
   const afterByGroup: { [groupBucketId: string]: CollarsPreviewLeg[] } = {};
   const txnByGroup: { [groupBucketId: string]: CollarsPreviewTxnLeg[] } = {};

   const touch = (groupBucketId: string) => {
      const key = groupBucketId || '';
      // Key-PRESENCE check, never isVoid: isVoid([]) is true, and a group with an empty Before column
      // keeps beforeByGroup[key] === [] through the After/Transaction walks — isVoid would re-register
      // the group on every later touch (duplicate preview rows) and wipe the legs already collected.
      if (beforeByGroup[key] === undefined) {
         beforeByGroup[key] = [];
         afterByGroup[key] = [];
         txnByGroup[key] = [];
         groupOrder.push(key);
      }
      return key;
   };

   // Before column — current live legs (CollarsEditableLeg face value).
   for (let i = 0; i < beforeLegs.length; i++) {
      const leg = beforeLegs[i];
      const key = touch(leg.groupBucketId);
      beforeByGroup[key].push(mapEditableLeg(leg));
   }

   // After column — edited / saved end-state legs, each bucketed by its OWN leaf group.
   for (let i = 0; i < afterLegs.length; i++) {
      const leg = afterLegs[i];
      const key = touch(leg.groupBucketId);
      afterByGroup[key].push(mapAfterStateLeg(leg));
   }

   // Transaction column — derived deltas per leaf group.
   for (let g = 0; g < groupTxns.length; g++) {
      const grp = groupTxns[g];
      const key = touch(grp.groupBucketId);
      const deltas = grp.deltas || [];
      for (let d = 0; d < deltas.length; d++) {
         txnByGroup[key].push(mapTxnLeg(deltas[d]));
      }
   }

   const rows: CollarsAfterStateRow[] = [];
   for (let i = 0; i < groupOrder.length; i++) {
      const key = groupOrder[i];
      const before = beforeByGroup[key];
      const transaction = txnByGroup[key];
      const after = afterByGroup[key];

      // Drop rows empty in every column (nothing to show for that group).
      if (before.length === 0 && transaction.length === 0 && after.length === 0) {
         continue;
      }

      const comboInfo = combos[key];
      rows.push({
         groupBucketId: key,
         // NEVER render the raw bucket id: a group missing from the context map gets a neutral label
         // (an unrouted new leg lands here until the operator picks its Target Group).
         label: names[key] || (key ? 'Unnamed group' : 'Unassigned'),
         color: undefined,
         isAsset: rowIsAsset(before, transaction, after),
         // No derived deltas => this bucket is untouched by the plan; the preview dims it (readonly look).
         unchanged: transaction.length === 0,
         comboBucketId: comboInfo ? comboInfo.comboBucketId : undefined,
         comboName: comboInfo ? comboInfo.comboName : undefined,
         before,
         transaction,
         after
      });
   }

   return orderByCombo(rows);
}

// --- row helpers -----------------------------------------------------------------------------------------

/** A row renders the simple ASSET table when every leg in all three columns is an asset leg. */
function rowIsAsset(
   before: CollarsPreviewLeg[],
   transaction: CollarsPreviewTxnLeg[],
   after: CollarsPreviewLeg[]
): boolean {
   let total = 0;
   for (let i = 0; i < before.length; i++) { if (before[i].kind !== KIND_ASSET) { return false; } total++; }
   for (let i = 0; i < transaction.length; i++) { if (transaction[i].kind !== KIND_ASSET) { return false; } total++; }
   for (let i = 0; i < after.length; i++) { if (after[i].kind !== KIND_ASSET) { return false; } total++; }
   return total > 0;
}

/**
 * The combo-bucketing key of a row: the real combo id, or a per-group pseudo key for rows without combo
 * context (so unrelated context-less rows never merge under one separator). SINGLE source of truth — the
 * preview's separator logic (multiCombo / isComboStart) must bucket exactly like orderByCombo here.
 */
export function comboKeyOf(row: CollarsAfterStateRow): string {
   return row.comboBucketId || ('__none__' + row.groupBucketId);
}

/** Stable re-order: rows grouped by first-seen combo (comboKeyOf keeps context-less rows in own slots). */
function orderByCombo(rows: CollarsAfterStateRow[]): CollarsAfterStateRow[] {
   const comboOrder: string[] = [];
   const byCombo: { [comboKey: string]: CollarsAfterStateRow[] } = {};
   for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const comboKey = comboKeyOf(row);
      if (isVoid(byCombo[comboKey])) {
         byCombo[comboKey] = [];
         comboOrder.push(comboKey);
      }
      byCombo[comboKey].push(row);
   }
   const out: CollarsAfterStateRow[] = [];
   for (let c = 0; c < comboOrder.length; c++) {
      const bucket = byCombo[comboOrder[c]];
      for (let r = 0; r < bucket.length; r++) { out.push(bucket[r]); }
   }
   return out;
}

// --- leg mappers ---------------------------------------------------------------------------------------

/** A current/Before leg (CollarsEditableLeg) -> preview leg, enriching option attrs from its ticker code. */
function mapEditableLeg(leg: CollarsEditableLeg): CollarsPreviewLeg {
   const out: CollarsPreviewLeg = {
      ticker: leg.tradingInstrumentCode,
      kind: leg.kind,
      qty: Math.abs(leg.qty),
      side: leg.side
   };
   applyOptionAttrs(out, leg.optionType, leg.strike, leg.expiration, leg.tradingInstrumentCode);
   return out;
}

/** An end-state leg (CollarsAfterStateLeg) -> preview leg, enriching option attrs from its ticker code. */
function mapAfterStateLeg(leg: CollarsAfterStateLeg): CollarsPreviewLeg {
   const out: CollarsPreviewLeg = {
      ticker: leg.tradingInstrumentCode,
      kind: leg.kind,
      qty: Math.abs(leg.qty),
      side: leg.side
   };
   applyOptionAttrs(out, leg.optionType, leg.strike, leg.expiration, leg.tradingInstrumentCode);
   return out;
}

/** A derived delta (CollarsTransactionDelta) -> transaction preview leg (Action/Qty/Type/Expiration/Strike). */
function mapTxnLeg(delta: CollarsTransactionDelta): CollarsPreviewTxnLeg {
   const out: CollarsPreviewTxnLeg = {
      operation: delta.action,
      groupBucketId: delta.groupBucketId,
      ticker: delta.tradingInstrumentCode,
      kind: delta.kind,
      side: delta.side,
      qty: Math.abs(delta.qty)
   };
   const enriched = enrichFromCode(delta.optionType, delta.strike, delta.expiration, delta.tradingInstrumentCode);
   if (!isVoid(enriched.type)) { out.type = enriched.type; }
   if (!isVoid(enriched.expiration)) { out.expiration = enriched.expiration; }
   if (isPresent(enriched.strike)) { out.strike = enriched.strike; }
   return out;
}

/**
 * Fill a preview leg's type/strike/expiration. When the leg already carries them, use them; otherwise the
 * values live in the option ticker code (current-position legs) — parse them out via parseOptionTicker.
 */
function applyOptionAttrs(
   out: CollarsPreviewLeg,
   optionType: string,
   strike: number,
   expiration: any,
   tradingInstrumentCode: string
): void {
   const enriched = enrichFromCode(optionType, strike, expiration, tradingInstrumentCode);
   if (!isVoid(enriched.type)) { out.type = enriched.type; }
   if (isPresent(enriched.strike)) { out.strike = enriched.strike; }
   if (!isVoid(enriched.expiration)) { out.expiration = enriched.expiration; }
}

/**
 * Normalize an expiration to the DISPLAY string the etsFriendlyDatePipe can format. Rolled / picker-driven
 * legs carry an OptionExpirationDescriptor OBJECT in `expiration` (the dialog edit primitives store the
 * descriptor); feeding that object to the pipe breaks the row render (makeFullExpirationDate regex/Luxon
 * expect a string -> blank Expiration + dead downstream cells). Strings pass through untouched.
 */
function expirationDisplayOf(expiration: any): any {
   if (expiration && typeof expiration === 'object' && expiration.optionExpirationDate) {
      return expiration.optionExpirationDate;
   }
   return expiration;
}

/**
 * Resolve type/strike/expiration: prefer the explicit values; for any still absent, parse the option ticker
 * code. Asset legs (non-option codes) parse to undefined and stay bare — exactly what the table wants.
 */
function enrichFromCode(
   optionType: string,
   strike: number,
   expiration: any,
   tradingInstrumentCode: string
): { type?: string; strike?: number; expiration?: any } {

   let type = optionType;
   let strk = strike;
   let exp = expirationDisplayOf(expiration);

   const needsType = isVoid(type);
   const needsStrike = !isPresent(strk);
   const needsExp = isVoid(exp);

   if ((needsType || needsStrike || needsExp) && !isVoid(tradingInstrumentCode)) {
      const parsed = parseOptionTicker(tradingInstrumentCode);
      if (!isVoid(parsed)) {
         if (needsType) { type = parsed.type; }
         if (needsStrike) { strk = parsed.strike; }
         if (needsExp) { exp = parsed.expiration; }
      }
   }

   const result: { type?: string; strike?: number; expiration?: any } = {};
   if (!isVoid(type)) { result.type = type; }
   if (isPresent(strk)) { result.strike = strk; }
   if (!isVoid(exp)) { result.expiration = exp; }
   return result;
}

/** Strict presence for numeric optionals: only null/undefined are absent (0 is a real strike value). */
function isPresent(value: any): boolean {
   return value !== null && value !== undefined;
}
